import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Physics constants ──────────────────────────────────────────────────────────
const MU = 3.986e14;
const RE = 6371e3;
const M_SAT = 12;
const M_MASS = 1;
const F_COIL = 0.8;
const T_COIL = 0.004;
const J_COIL = 0.0032;
const DV_HULL = 0.000267;   // m/s per stroke
const VC = 7644.4;           // m/s circular velocity at 450 km
const T_ORB = 5606;          // s
const G_450 = 8.567;         // m/s² at 450 km
const VR_MAX = 8.554;        // m/s radial velocity built during gravity phase
const DE_DAMPER = 0.002281;  // J/kg per cycle
const D_APOGEE = 0.7277;     // m per cycle
const D_PERIGEE = 0.7267;    // m per cycle (decrease)
const CYCLES_TO_10KM = 13741;
const COLD_GAS_BURNS = 215;
const COLD_GAS_PROP = 97.9;  // g

// ── Color scheme ──────────────────────────────────────────────────────────────
const C = {
  bg: '#050e10',
  card: '#0b1a1e',
  border: '#1a3a30',
  hi: '#00ff88',
  acc: '#0d9e6a',
  repulse: '#e24a4a',
  gravity: '#6ad4ff',
  damp: '#e2c84a',
  text: '#b0d0c0',
  muted: '#4a7a6a',
  earth: '#0a1e3a',
};

// ── Phase durations (wall-clock seconds at 1× speed) ────────────────────────
const PHASE_DURATIONS = { repulse: 0.8, gravity: 1.0, damp: 0.6, idle: 0.7 };
const PHASE_ORDER = ['repulse', 'gravity', 'damp', 'idle'];

// ── Initial state ─────────────────────────────────────────────────────────────
const INIT_STATE = {
  running: false,
  speed: 1,
  cycle: 0,
  phase: 'idle',
  phaseT: 0,
  apogeeKm: 450.0,
  perigeeKm: 450.0,
  eccentricity: 0,
  vrGauge: 0,
  orbits: [],
  pendulumAngle: 3,
  pendulumPhaseT: 0,
};

function computeEcc(apoKm, periKm) {
  const r_apo = RE + apoKm * 1000;
  const r_peri = RE + periKm * 1000;
  return (r_apo - r_peri) / (r_apo + r_peri);
}

function nextState(s, simDt) {
  if (!s.running) return s;

  const dur = PHASE_DURATIONS[s.phase] || 0.3;
  let newPhaseT = s.phaseT + simDt / dur;
  let phase = s.phase;
  let cycle = s.cycle;
  let apogeeKm = s.apogeeKm;
  let perigeeKm = s.perigeeKm;
  let vrGauge = s.vrGauge;
  let orbits = s.orbits;

  while (newPhaseT >= 1) {
    newPhaseT -= 1;
    const idx = PHASE_ORDER.indexOf(phase);
    const nextIdx = (idx + 1) % PHASE_ORDER.length;
    phase = PHASE_ORDER[nextIdx];

    if (phase === 'repulse') {
      // completed a full cycle
      const prevOrbit = { apogeeKm, perigeeKm };
      cycle = cycle + 1;
      if (cycle <= CYCLES_TO_10KM) {
        apogeeKm = apogeeKm + D_APOGEE / 1000;
        perigeeKm = perigeeKm - D_PERIGEE / 1000;
        orbits = [...orbits, prevOrbit].slice(-5);
      }
      vrGauge = 0;
    }
    if (phase === 'damp') {
      vrGauge = -VR_MAX;
    }
  }

  // Compute vrGauge during gravity phase
  if (phase === 'gravity') {
    vrGauge = -VR_MAX * newPhaseT;
  }

  const pendulumAngle = 3 + (cycle / CYCLES_TO_10KM) * 42;
  const eccentricity = computeEcc(apogeeKm, perigeeKm);

  return {
    ...s,
    phase,
    phaseT: newPhaseT,
    cycle,
    apogeeKm,
    perigeeKm,
    eccentricity,
    vrGauge,
    orbits,
    pendulumAngle,
  };
}

// ── Orbit ellipse helpers ─────────────────────────────────────────────────────
function orbitGeometry(apoKm, periKm) {
  const r_apo = RE + apoKm * 1000;
  const r_peri = RE + periKm * 1000;
  const a = (r_apo + r_peri) / 2;
  const ecc = (r_apo - r_peri) / (r_apo + r_peri);
  const b = a * Math.sqrt(Math.max(0, 1 - ecc * ecc));
  const sc = 155 / (RE + 465000);
  const a_px = a * sc;
  const b_px = b * sc;
  const c_px = a * ecc * sc;
  return { a_px, b_px, c_px, ecc };
}

// ── Pendulum bob position ─────────────────────────────────────────────────────
function pendulumBobPos(phase, phaseT, thetaDeg, pivot) {
  const ARM = 200;
  const theta = (thetaDeg * Math.PI) / 180;
  let angle; // angle from vertical, positive = right (apogee side)
  if (phase === 'repulse') {
    angle = theta; // positive extreme
  } else if (phase === 'gravity') {
    // swing from +theta to -theta
    angle = theta * Math.cos(Math.PI * phaseT);
  } else if (phase === 'damp') {
    angle = -theta; // negative extreme
  } else {
    // idle: swing back from -theta to +theta
    angle = -theta + 2 * theta * phaseT;
  }
  return {
    x: pivot.x + ARM * Math.sin(angle),
    y: pivot.y + ARM * Math.cos(angle),
    angle,
  };
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function EMMAOrbitRaise() {
  const [state, setState] = useState(INIT_STATE);
  const rafRef = useRef(null);
  const lastTsRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Animation loop
  useEffect(() => {
    if (!state.running) {
      lastTsRef.current = null;
      return;
    }
    const tick = (ts) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const wallDt = Math.min((ts - lastTsRef.current) / 1000, 0.1);
      lastTsRef.current = ts;
      const simDt = wallDt * stateRef.current.speed;
      setState(s => nextState({ ...s, running: true }, simDt));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [state.running]);

  const handleStartPause = () => setState(s => ({ ...s, running: !s.running }));
  const handleReset = () => {
    setState({ ...INIT_STATE });
    lastTsRef.current = null;
  };
  const handleSpeed = (e) => setState(s => ({ ...s, speed: Number(e.target.value) }));

  const { cycle, phase, phaseT, apogeeKm, perigeeKm, eccentricity, vrGauge, orbits, pendulumAngle, speed, running } = state;

  // ── Derived values ──────────────────────────────────────────────────────────
  const progress = cycle / CYCLES_TO_10KM;
  const coldGasBurns = (cycle * COLD_GAS_BURNS / CYCLES_TO_10KM).toFixed(1);
  const coldGasProp = (cycle * COLD_GAS_PROP / CYCLES_TO_10KM).toFixed(1);

  // Pendulum
  const PIVOT = { x: 195, y: 40 };
  const ARM = 200;
  const thetaRad = (pendulumAngle * Math.PI) / 180;
  const bobPos = pendulumBobPos(phase, phaseT, pendulumAngle, PIVOT);
  const phaseColor = phase === 'repulse' ? C.repulse : phase === 'gravity' ? C.gravity : phase === 'damp' ? C.damp : C.text;

  // Orbit geometry (current)
  const orb = orbitGeometry(apogeeKm, perigeeKm);
  const EARTH_CX = 585;
  const EARTH_CY = 210;

  // Satellite position on orbit (simplified angular position)
  let satAngleDeg = 0;
  if (phase === 'repulse') satAngleDeg = 180; // apogee (leftmost)
  else if (phase === 'gravity') satAngleDeg = 180 - 10 * phaseT;
  else if (phase === 'damp') satAngleDeg = 170 - 5 * phaseT;
  else satAngleDeg = 165 + 15 * phaseT;
  const satAngleRad = (satAngleDeg * Math.PI) / 180;
  const satX = EARTH_CX - orb.c_px + orb.a_px * Math.cos(satAngleRad);
  const satY = EARTH_CY + orb.b_px * Math.sin(satAngleRad);

  // Mass position in satellite panel
  const MASS_TOP_Y = 135;  // coil bottom
  const MASS_BOT_Y = 255;  // damper top
  let massY;
  if (phase === 'repulse') {
    // At apogee: coil fires — mass accelerates from coil toward damper (ease-in)
    massY = MASS_TOP_Y + (MASS_BOT_Y - MASS_TOP_Y) * Math.pow(phaseT, 0.6);
  } else if (phase === 'gravity') {
    // Coasting: mass floats at damper end
    massY = MASS_BOT_Y;
  } else if (phase === 'damp') {
    // After crossing apogee: mass slams into damper — impact pulse then settles
    const imp = Math.sin(Math.PI * Math.min(phaseT * 4, 1));
    massY = MASS_BOT_Y + imp * 12;
  } else {
    // Idle: mass travels back up from damper to coil (ease-out spring reset)
    massY = MASS_BOT_Y + (MASS_TOP_Y - MASS_BOT_Y) * Math.pow(phaseT, 1.6);
  }

  // vr gauge fill
  const vrPct = Math.abs(vrGauge) / VR_MAX;
  const VR_GAUGE_H = 160;
  const VR_GAUGE_TOP = 80;

  // Stage bar widths
  const totalDur = 0.3 + 1.0 + 0.3;
  const repW = Math.round(380 * 0.3 / totalDur);
  const gravW = Math.round(380 * 1.0 / totalDur);
  const dampW = Math.round(380 * 0.3 / totalDur);

  const phaseLabel = phase === 'repulse' ? 'COIL FIRING' : phase === 'gravity' ? 'GRAVITY ACTING' : phase === 'damp' ? 'DAMPER FIRING' : 'IDLE';

  const arcPath = (() => {
    const r = ARM;
    const x1 = PIVOT.x + r * Math.sin(-thetaRad);
    const y1 = PIVOT.y + r * Math.cos(-thetaRad);
    const x2 = PIVOT.x + r * Math.sin(thetaRad);
    const y2 = PIVOT.y + r * Math.cos(thetaRad);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
  })();

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: 'monospace', color: C.text, padding: '16px', boxSizing: 'border-box' }}>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 22, color: C.hi, letterSpacing: 3, fontWeight: 700 }}>EMMS PROPELLANT-FREE ORBIT RAISE</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Electromagnetic Mass Shifting System — EP-ADCS Patent Simulator</div>
      </div>

      {/* Three panels */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>

        {/* ── Panel 1: Pendulum + Orbit ─────────────────────────────────────── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, flex: '0 0 auto' }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 4, letterSpacing: 2 }}>PENDULUM + ORBIT COUPLING</div>
          <svg width={780} height={420} style={{ display: 'block' }}>
            <defs>
              <radialGradient id="earthGrad" cx="40%" cy="40%">
                <stop offset="0%" stopColor="#1a4a8a" />
                <stop offset="100%" stopColor={C.earth} />
              </radialGradient>
              <filter id="glowGreen">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="glowYellow">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="glowBlue">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Background fill */}
            <rect width={780} height={420} fill={C.bg} rx={4} />

            {/* ── LEFT HALF: Pendulum ── */}
            {/* Swing arc */}
            <path d={arcPath} fill="none" stroke="#2a4a3a" strokeWidth={1.5} strokeDasharray="4 4" />

            {/* Bottom label at arc nadir */}
            <text x={PIVOT.x} y={PIVOT.y + ARM + 18} textAnchor="middle" fill={C.gravity} fontSize={11}>v = max</text>

            {/* Extreme labels */}
            {(() => {
              const rx = PIVOT.x + ARM * Math.sin(thetaRad);
              const ry = PIVOT.y + ARM * Math.cos(thetaRad);
              const lx = PIVOT.x - ARM * Math.sin(thetaRad);
              const ly = PIVOT.y + ARM * Math.cos(thetaRad);
              return (
                <>
                  <text x={rx + 8} y={ry} fill={C.repulse} fontSize={10} dominantBaseline="middle">repulsion</text>
                  <text x={rx + 8} y={ry + 12} fill={C.repulse} fontSize={10}>fires</text>
                  <text x={lx - 8} y={ly} fill={C.damp} fontSize={10} textAnchor="end" dominantBaseline="middle">damper</text>
                  <text x={lx - 8} y={ly + 12} fill={C.damp} fontSize={10} textAnchor="end">fires</text>
                </>
              );
            })()}

            {/* Arm line */}
            <line x1={PIVOT.x} y1={PIVOT.y} x2={bobPos.x} y2={bobPos.y} stroke={C.muted} strokeWidth={2} />

            {/* Pivot */}
            <circle cx={PIVOT.x} cy={PIVOT.y} r={5} fill={C.text} />

            {/* Gravity arrow on bob */}
            <line x1={bobPos.x} y1={bobPos.y + 18} x2={bobPos.x} y2={bobPos.y + 48} stroke={C.gravity} strokeWidth={2} markerEnd="url(#arrowBlue)" />

            {/* Bob */}
            <circle
              cx={bobPos.x} cy={bobPos.y} r={16}
              fill={phaseColor}
              filter={phase !== 'idle' ? 'url(#glowGreen)' : undefined}
              opacity={0.9}
            />
            <text x={bobPos.x} y={bobPos.y} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={10} fontWeight="bold">M</text>

            {/* Arrow markers */}
            <defs>
              <marker id="arrowBlue" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill={C.gravity} />
              </marker>
            </defs>

            {/* Pendulum labels */}
            <text x={10} y={400} fill={C.text} fontSize={13}>Pendulum θ = {pendulumAngle.toFixed(1)}°  amplitude = {(ARM * Math.sin(thetaRad) * 0.05).toFixed(2)} cm</text>

            {/* ── CENTER DIVIDER ── */}
            <line x1={390} y1={0} x2={390} y2={380} stroke={C.border} strokeWidth={1} strokeDasharray="6 4" />

            {/* Center annotations */}
            {[
              { y: 120, label: 'Turning point ↔ Apogee' },
              { y: 200, label: 'Gravity restores ↔ Gravity restores' },
              { y: 280, label: 'Amplitude grows ↔ Eccentricity grows' },
            ].map(({ y, label }) => (
              <text key={y} x={390} y={y} textAnchor="middle" fill={C.muted} fontSize={11}>{label}</text>
            ))}

            {/* ── RIGHT HALF: Orbit ── */}
            {/* Earth */}
            <circle cx={EARTH_CX} cy={EARTH_CY} r={40} fill="url(#earthGrad)" />
            <circle cx={EARTH_CX} cy={EARTH_CY} r={40} fill="none" stroke="#1a4a8a" strokeWidth={1.5} />
            <text x={EARTH_CX} y={EARTH_CY + 4} textAnchor="middle" fill={C.gravity} fontSize={11} dominantBaseline="middle">Earth</text>

            {/* Orbit history (faded) */}
            {orbits.map((o, i) => {
              const og = orbitGeometry(o.apogeeKm, o.perigeeKm);
              const opacity = 0.05 + (i / orbits.length) * 0.15;
              return (
                <ellipse
                  key={i}
                  cx={EARTH_CX - og.c_px}
                  cy={EARTH_CY}
                  rx={og.a_px}
                  ry={og.b_px}
                  fill="none"
                  stroke={C.acc}
                  strokeWidth={1}
                  opacity={opacity}
                />
              );
            })}

            {/* Current orbit ellipse */}
            <ellipse
              cx={EARTH_CX - orb.c_px}
              cy={EARTH_CY}
              rx={orb.a_px}
              ry={orb.b_px}
              fill="none"
              stroke={C.acc}
              strokeWidth={1.5}
            />

            {/* Apogee dot */}
            {(() => {
              const apX = EARTH_CX - orb.c_px - orb.a_px;
              const apY = EARTH_CY;
              return (
                <>
                  <circle cx={apX} cy={apY} r={6}
                    fill={C.hi}
                    filter={phase === 'repulse' ? 'url(#glowGreen)' : undefined}
                  />
                  <text x={apX - 8} y={apY - 12} textAnchor="middle" fill={C.hi} fontSize={11}>APO {apogeeKm.toFixed(3)} km</text>
                </>
              );
            })()}

            {/* Perigee dot */}
            {(() => {
              const peX = EARTH_CX - orb.c_px + orb.a_px;
              const peY = EARTH_CY;
              return (
                <>
                  <circle cx={peX} cy={peY} r={6}
                    fill={C.gravity}
                    filter={phase === 'damp' ? 'url(#glowYellow)' : undefined}
                  />
                  <text x={peX + 8} y={peY - 12} textAnchor="start" fill={C.gravity} fontSize={11}>PER {perigeeKm.toFixed(3)} km</text>
                </>
              );
            })()}

            {/* Satellite dot */}
            <circle cx={satX} cy={satY} r={5} fill={phaseColor} filter="url(#glowGreen)" />
            <circle cx={satX} cy={satY} r={8} fill="none" stroke={phaseColor} strokeWidth={1} opacity={0.5} />

            {/* Orbit labels bottom */}
            <text x={490} y={400} fill={C.text} fontSize={13}>Orbit apogee = {apogeeKm.toFixed(3)} km  ecc = {eccentricity.toFixed(6)}</text>
          </svg>
        </div>

        {/* ── Panel 2: Satellite Mechanism ─────────────────────────────────── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, flex: '0 0 auto', width: 380 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 4, letterSpacing: 2 }}>SATELLITE MECHANISM</div>
          <svg width={380} height={380} style={{ display: 'block' }}>
            <rect width={380} height={380} fill={C.bg} rx={4} />

            {/* Satellite body */}
            <rect x={140} y={80} width={100} height={240} fill="#0d2030" stroke={C.border} strokeWidth={2} rx={3} />

            {/* Solar panels */}
            <rect x={50} y={175} width={88} height={30} fill="#1a3a6a" stroke={C.border} strokeWidth={1} rx={2} />
            <rect x={242} y={175} width={88} height={30} fill="#1a3a6a" stroke={C.border} strokeWidth={1} rx={2} />
            <text x={94} y={194} textAnchor="middle" fill={C.muted} fontSize={10}>SOLAR</text>
            <text x={286} y={194} textAnchor="middle" fill={C.muted} fontSize={10}>SOLAR</text>

            {/* Track dashed line */}
            <line x1={190} y1={112} x2={190} y2={288} stroke={C.muted} strokeWidth={1} strokeDasharray="4 3" />

            {/* COIL */}
            <rect
              x={155} y={90} width={70} height={24}
              fill={phase === 'repulse' ? C.repulse : '#1a3a30'}
              stroke={phase === 'repulse' ? C.repulse : C.border}
              strokeWidth={phase === 'repulse' ? 2 : 1}
              rx={3}
              filter={phase === 'repulse' ? 'url(#glowGreen)' : undefined}
            />
            <text x={190} y={106} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="bold">EM COIL</text>

            {/* MASS (moving) */}
            <rect
              x={160} y={massY} width={60} height={16}
              fill="#c0d0c8"
              stroke="#fff"
              strokeWidth={1}
              rx={2}
            />
            <text x={190} y={massY + 11} textAnchor="middle" fill="#0a1e1a" fontSize={10} fontWeight="bold">MASS</text>

            {/* DAMPER */}
            <rect
              x={155} y={276} width={70} height={24}
              fill={phase === 'damp' ? C.damp : '#1a3a30'}
              stroke={phase === 'damp' ? C.damp : C.border}
              strokeWidth={phase === 'damp' ? 2 : 1}
              rx={3}
              filter={phase === 'damp' ? 'url(#glowYellow)' : undefined}
            />
            <text x={190} y={292} textAnchor="middle" fill={phase === 'damp' ? '#000' : '#fff'} fontSize={11} fontWeight="bold">DAMPER</text>

            {/* ── External arrows ── */}
            {phase === 'repulse' && (
              <>
                <line x1={248} y1={180} x2={248} y2={130} stroke={C.hi} strokeWidth={2.5} markerEnd="url(#arrowHi)" />
                <text x={255} y={145} fill={C.hi} fontSize={10}>hull outward</text>
                <text x={255} y={157} fill={C.hi} fontSize={10}>dv=0.000267 m/s</text>
              </>
            )}
            {phase === 'gravity' && (
              <>
                <line x1={132} y1={150} x2={132} y2={210} stroke={C.gravity} strokeWidth={2.5} markerEnd="url(#arrowGrav)" />
                <text x={30} y={170} fill={C.gravity} fontSize={10}>g = 8.567</text>
                <text x={30} y={182} fill={C.gravity} fontSize={10}>m/s²</text>
              </>
            )}
            {phase === 'damp' && (
              <>
                <line x1={248} y1={200} x2={248} y2={250} stroke={C.damp} strokeWidth={2.5} markerEnd="url(#arrowDamp)" />
                <text x={255} y={220} fill={C.damp} fontSize={10}>hull inward</text>
                <text x={255} y={232} fill={C.damp} fontSize={10}>harvesting KE</text>
              </>
            )}

            {/* Arrow defs */}
            <defs>
              <marker id="arrowHi" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                <path d="M0,8 L4,0 L8,8 Z" fill={C.hi} />
              </marker>
              <marker id="arrowGrav" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill={C.gravity} />
              </marker>
              <marker id="arrowDamp" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                <path d="M0,0 L4,8 L8,0 Z" fill={C.damp} />
              </marker>
            </defs>

            {/* ── vr gauge (right side) ── */}
            <text x={350} y={75} textAnchor="middle" fill={C.gravity} fontSize={12} fontWeight="bold">vr</text>
            <text x={350} y={88} textAnchor="middle" fill={C.muted} fontSize={9}>0</text>
            <rect x={340} y={90} width={20} height={VR_GAUGE_H} fill="#0a1a20" stroke={C.border} strokeWidth={1} rx={2} />
            <rect
              x={340}
              y={90 + VR_GAUGE_H * (1 - vrPct)}
              width={20}
              height={VR_GAUGE_H * vrPct}
              fill={C.gravity}
              rx={2}
              opacity={0.85}
            />
            <text x={350} y={260} textAnchor="middle" fill={C.muted} fontSize={9}>−8.554</text>
            <text x={350} y={272} textAnchor="middle" fill={C.muted} fontSize={9}>m/s</text>
            <text x={350} y={285} textAnchor="middle" fill={C.gravity} fontSize={11}>{vrGauge.toFixed(3)}</text>

            {/* Work done text */}
            {phase === 'repulse' && (
              <>
                <text x={190} y={340} textAnchor="middle" fill={C.repulse} fontSize={12}>dE = dv × 0 = 0 J/kg</text>
                <text x={190} y={354} textAnchor="middle" fill={C.muted} fontSize={11}>(v_radial = 0 at repulsion)</text>
              </>
            )}
            {phase === 'gravity' && (
              <>
                <text x={190} y={340} textAnchor="middle" fill={C.gravity} fontSize={12}>v_radial building: 0 → −8.554 m/s</text>
                <text x={190} y={354} textAnchor="middle" fill={C.gravity} fontSize={11}>current: {vrGauge.toFixed(3)} m/s</text>
              </>
            )}
            {phase === 'damp' && (
              <>
                <text x={190} y={335} textAnchor="middle" fill={C.damp} fontSize={13} fontWeight="bold">dE = 0.000267 × 8.554</text>
                <text x={190} y={351} textAnchor="middle" fill={C.damp} fontSize={15} fontWeight="bold">= 0.002281 J/kg</text>
              </>
            )}
            {phase === 'idle' && (
              <text x={190} y={345} textAnchor="middle" fill={C.muted} fontSize={12}>Cycle {cycle} complete</text>
            )}
          </svg>

          {/* Stage timeline bar */}
          <div style={{ display: 'flex', height: 28, marginTop: 4, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: repW, background: phase === 'repulse' ? C.repulse : '#2a1a1a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: phase === 'repulse' ? '#fff' : C.muted,
              transition: 'background 0.2s',
              position: 'relative', overflow: 'hidden',
            }}>
              {phase === 'repulse' && (
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${phaseT * 100}%`, background: 'rgba(255,255,255,0.15)' }} />
              )}
              COIL FIRES
            </div>
            <div style={{
              width: gravW, background: phase === 'gravity' ? C.gravity : '#0a1a2a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: phase === 'gravity' ? '#000' : C.muted,
              position: 'relative', overflow: 'hidden',
            }}>
              {phase === 'gravity' && (
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${phaseT * 100}%`, background: 'rgba(0,0,0,0.2)' }} />
              )}
              GRAVITY ACTS 1s
            </div>
            <div style={{
              width: dampW, background: phase === 'damp' ? C.damp : '#1a1a0a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: phase === 'damp' ? '#000' : C.muted,
              position: 'relative', overflow: 'hidden',
            }}>
              {phase === 'damp' && (
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${phaseT * 100}%`, background: 'rgba(0,0,0,0.2)' }} />
              )}
              DAMPER FIRES
            </div>
          </div>

          {/* Energy flow box */}
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#0a1a14', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12 }}>
            <div style={{ color: C.hi, fontWeight: 'bold', marginBottom: 4 }}>ENERGY FLOW:</div>
            <div style={{ color: C.repulse }}>Battery → Coil → trigger</div>
            <div style={{ color: C.gravity }}>Gravity → KE = 8.554 m/s</div>
            <div style={{ color: C.damp }}>Damper → orbital energy</div>
            <div style={{ color: C.hi, marginTop: 4 }}>NET: +0.002281 J/kg</div>
            <div style={{ color: C.hi }}>Propellant: 0g</div>
          </div>
        </div>

        {/* ── Panel 3: Live Data ────────────────────────────────────────────── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, flex: '0 0 380px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, color: C.muted, letterSpacing: 2 }}>LIVE TELEMETRY</div>

          {/* Section A: Telemetry */}
          <div style={{ background: '#070f12', border: `1px solid ${C.border}`, borderRadius: 4, padding: '10px 12px', fontSize: 13, lineHeight: 1.8 }}>
            <div style={{ color: C.hi, fontSize: 15, fontWeight: 'bold' }}>EMMS CYCLE: {cycle.toLocaleString()}</div>
            <div style={{ color: C.text, marginTop: 6 }}>ORBIT STATE:</div>
            <div style={{ paddingLeft: 12 }}>
              <div>Apogee: <span style={{ color: C.hi, fontSize: 15 }}>{apogeeKm.toFixed(3)} km</span> <span style={{ color: C.muted, fontSize: 11 }}>(+0.7277m)</span></div>
              <div>Perigee: <span style={{ color: C.gravity, fontSize: 15 }}>{perigeeKm.toFixed(3)} km</span> <span style={{ color: C.muted, fontSize: 11 }}>(−0.7267m)</span></div>
              <div>Eccentricity: <span style={{ color: C.text }}>{eccentricity.toFixed(6)}</span></div>
            </div>
            <div style={{ color: C.text, marginTop: 6 }}>ENERGY:</div>
            <div style={{ paddingLeft: 12 }}>
              <div>Repulsion: <span style={{ color: C.muted }}>0.000000 J/kg</span></div>
              <div>Damper: <span style={{ color: C.damp }}>0.002281 J/kg</span></div>
              <div>Net: <span style={{ color: C.hi, fontWeight: 'bold' }}>+0.002281 J/kg</span></div>
            </div>
            <div style={{ marginTop: 6 }}>
              PROPELLANT: <span style={{ color: C.hi, fontSize: 16, fontWeight: 'bold' }}>0.000 g</span>
              <span style={{ color: C.hi, fontSize: 11, marginLeft: 8 }}>← zero always</span>
            </div>
          </div>

          {/* Section B: Work equation */}
          <div style={{
            background: phase === 'damp' ? '#1a1500' : '#070f12',
            border: `1px solid ${phase === 'damp' ? C.damp : C.border}`,
            borderRadius: 4, padding: '10px 12px', fontSize: 13, lineHeight: 1.7,
            transition: 'background 0.3s, border-color 0.3s',
          }}>
            <div style={{ color: phase === 'damp' ? C.damp : C.text, fontWeight: 'bold', marginBottom: 4 }}>WORK EQUATION</div>
            <div style={{ color: C.text }}>Work = Impulse × Velocity</div>
            <div style={{ color: C.text, paddingLeft: 8 }}>= 0.000267 m/s × 8.554 m/s</div>
            <div style={{ color: phase === 'damp' ? C.damp : C.hi, fontWeight: 'bold', paddingLeft: 8, fontSize: 15 }}>
              = 0.002281 J/kg
            </div>
            <div style={{ color: C.gravity, fontSize: 12, marginTop: 4 }}>← Gravity built the 8.554 m/s</div>
          </div>

          {/* Section C: Comparison bars */}
          <div style={{ background: '#070f12', border: `1px solid ${C.border}`, borderRadius: 4, padding: '10px 12px' }}>
            <div style={{ color: C.text, fontWeight: 'bold', marginBottom: 8, fontSize: 13 }}>COMPARISON</div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: C.hi, marginBottom: 3 }}>
                EMMS: {cycle.toLocaleString()} / 13,741 cycles  •  0g propellant
              </div>
              <div style={{ height: 12, background: '#0a2010', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(progress * 100, 100)}%`, background: C.hi, borderRadius: 6, transition: 'width 0.1s' }} />
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: C.damp, marginBottom: 3 }}>
                Cold gas: {parseFloat(coldGasBurns).toFixed(0)} / 215 burns  •  {coldGasProp}g propellant
              </div>
              <div style={{ height: 12, background: '#1a1000', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(progress * 100, 100)}%`, background: C.damp, borderRadius: 6, transition: 'width 0.1s' }} />
              </div>
            </div>

            <div style={{ fontSize: 12, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 6, marginTop: 4 }}>
              EMMS uses <span style={{ color: C.hi, fontWeight: 'bold' }}>NO propellant</span>.{' '}
              Cold gas would use <span style={{ color: C.damp }}>{coldGasProp}g</span> so far.
            </div>
          </div>

          {/* Section D: Patent claim */}
          <div style={{ background: '#070f12', border: `2px solid ${C.acc}`, borderRadius: 4, padding: '10px 12px', fontSize: 12, lineHeight: 1.6, marginTop: 'auto' }}>
            <div style={{ color: C.acc, fontWeight: 'bold', letterSpacing: 2, marginBottom: 6, fontSize: 13 }}>EP-ADCS PATENT CLAIM</div>
            <div style={{ color: C.text, fontStyle: 'italic' }}>
              "The electromagnetic mass shifting system raises orbital
              altitude without propellant by using gravity as the
              external energy source..."
            </div>
            <div style={{ marginTop: 8, color: C.hi, fontSize: 11 }}>
              Cycle {cycle} of {CYCLES_TO_10KM.toLocaleString()} to raise +10 km
            </div>
          </div>
        </div>
      </div>

      {/* ── Controls bar ───────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 20,
        marginTop: 14, padding: '12px 20px',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
        flexWrap: 'wrap',
      }}>
        <button
          onClick={handleStartPause}
          style={{
            padding: '8px 24px', fontSize: 15, fontWeight: 'bold',
            background: running ? '#2a1a1a' : '#0a2a18',
            color: running ? C.repulse : C.hi,
            border: `2px solid ${running ? C.repulse : C.hi}`,
            borderRadius: 5, cursor: 'pointer', letterSpacing: 2,
            fontFamily: 'monospace',
          }}
        >
          {running ? '⏸ PAUSE' : '▶ START'}
        </button>

        <button
          onClick={handleReset}
          style={{
            padding: '8px 20px', fontSize: 14,
            background: '#0a1a20', color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 5, cursor: 'pointer', letterSpacing: 1,
            fontFamily: 'monospace',
          }}
        >
          ↺ RESET
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: C.muted, fontSize: 13 }}>Speed:</span>
          <input
            type="range" min={1} max={50} value={speed}
            onChange={handleSpeed}
            style={{ width: 140, accentColor: C.hi }}
          />
          <span style={{ color: C.hi, fontSize: 15, fontWeight: 'bold', minWidth: 40 }}>{speed}×</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.muted, fontSize: 13 }}>Phase:</span>
          <span style={{
            fontSize: 14, fontWeight: 'bold', letterSpacing: 1,
            color: phaseColor, padding: '3px 10px',
            border: `1px solid ${phaseColor}`,
            borderRadius: 4,
          }}>
            {phaseLabel}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: C.muted, fontSize: 13 }}>Cycle:</span>
          <span style={{ color: C.text, fontSize: 15, fontWeight: 'bold' }}>{cycle.toLocaleString()}</span>
          <span style={{ color: C.muted, fontSize: 13 }}>/ 13,741</span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: C.muted }}>Apogee</div>
            <div style={{ fontSize: 16, color: C.hi, fontWeight: 'bold' }}>{apogeeKm.toFixed(3)} km</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: C.muted }}>Perigee</div>
            <div style={{ fontSize: 16, color: C.gravity, fontWeight: 'bold' }}>{perigeeKm.toFixed(3)} km</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: C.muted }}>Eccentricity</div>
            <div style={{ fontSize: 14, color: C.text }}>{eccentricity.toFixed(6)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
